import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  getAdditionalUserInfo,
  signInWithCredential,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  validatePassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import { debounce } from "es-toolkit";
import { useMockupStore } from "@/datastore/module/mockup.js";

const auth = getAuth();
const provider = new GoogleAuthProvider();

export default {
  async signinWithPopup() {
    const store = useMockupStore();
    const user = await signInWithPopup(auth, provider);
    const info = getAdditionalUserInfo(user);
    store.updateNewUser(info?.isNewUser);
    return { user, isNewUser: info?.isNewUser };
  },

  async signinWithCredential(response) {
    const store = useMockupStore();
    const credential = GoogleAuthProvider.credential(response.credential);
    const user = await signInWithCredential(auth, credential);
    const info = getAdditionalUserInfo(user);
    store.updateNewUser(info?.isNewUser);
    return { user, isNewUser: info?.isNewUser };
  },

  async sendSigninLink(email) {
    const actionCodeSettings = {
      // URL you want to redirect back to.
      url: `${window.location.origin}/login?email=${email}`,
      // This must be true.
      handleCodeInApp: true,
    };
    await sendSignInLinkToEmail(auth, email, actionCodeSettings);
  },

  async signinWithEmailLink(route) {
    if (isSignInWithEmailLink(auth, window.location.href)) {
      let email = route?.query?.email;
      if (!email) return;
      const user = await signInWithEmailLink(auth, email, window.location.href);
      const info = getAdditionalUserInfo(user);
      return { user, isNewUser: info?.isNewUser };
    }
  },

  async signinWithEmailAndPassword(email, password) {
    const store = useMockupStore();
    try {
      const status = await validatePassword(auth, password);
      if (status) {
        const user = await signInWithEmailAndPassword(auth, email, password);
        store.updateNewUser(false);
        return { user, isNewUser: false };
      } else {
        return { message: "login.passwordValidationFailed" };
      }
    } catch (e) {
      let message = null;
      if (e.code === "auth/invalid-credential") {
        message = "login.invalidEmailPassword";
      } else if (e.code === "auth/too-many-requests") {
        message = e.code;
      } else if (e.code === "auth/wrong-password") {
        message = "login.wrongPassword";
      } else if (e.code === "auth/user-not-found") {
        message = e.code;
      } else {
        message = "login.somethingWentWrong";
      }

      return { user: null, isNewUser: false, message };
    }
  },

  async signupWithEmailAndPassword(email, password) {
    const store = useMockupStore();
    try {
      const status = await validatePassword(auth, password);
      if (status) {
        const user = await createUserWithEmailAndPassword(
          auth,
          email,
          password,
        );
        store.updateNewUser(true);
        return { user, isNewUser: true, message: null };
      } else {
        return { message: "login.validationFailed" };
      }
    } catch (e) {
      let message = null;

      if (e.code === "auth/email-already-in-use") {
        message = e.code;
      } else {
        message = "login.somethingWentWrong";
      }

      return { user: null, isNewUser: true, message };
    }
  },

  validateUserEmail(email) {
    // Regular expression for a valid email address
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const isValid = emailRegex.test(email);
    if (!isValid) {
      return { status: false, message: "login.enterValidEmail" };
    }
    return { status: true };
  },

  validateUserPassword(password) {
    // Check for leading and trailing spaces and spaces within password
    if (password.trim() !== password || /\s/.test(password)) {
      return { message: "login.noSpacesInPassword" };
    }
    // Regular expressions for different password rules
    const uppercaseRegex = /[A-Z]/; // At least one uppercase letter
    const specialCharRegex = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/; // At least one special character

    if (!uppercaseRegex.test(password)) {
      return {
        message: "login.uppercaseRequirement",
      };
    }
    if (!specialCharRegex.test(password)) {
      return {
        message: "login.specialCharRequire",
      };
    }
    if (password.length < 6) {
      return { message: "login.passwordLengthRequirement" };
    }
    return { status: true };
  },

  usePasswordValidation(delay = 100) {
    const validatePassword = debounce(
      (password) => {
        const { status, message } = this.validateUserPassword(password);
        return { status, message };
      },
      delay,
      { leading: true },
    );

    return { validatePassword };
  },

  async passwordResetEmail(email) {
    try {
      await sendPasswordResetEmail(auth, email);
      return { message: "login.passwordResetSent" };
    } catch (e) {
      return { message: "login.passwordResetFailed" };
    }
  },
};
